
import { Player, Tournament, Match, NewsArticle, GameMode, Team, MatchMedia, TournamentMatch, TournamentStatus } from '../types';
import { ROSTER, PAST_TOURNAMENTS, INITIAL_NEWS, INITIAL_TEAMS, PAST_DOUBLES_TOURNAMENTS, INITIAL_DOUBLES_NEWS, MANUAL_BADGES } from '../data/seedData';
import { GameStore, GameState } from '../store/GameStore';
import { storageAdapter, safeStringify } from './storageAdapter';
import { db, auth } from '../lib/firebase';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { STORAGE_KEYS } from './storageKeys';

// Legacy keys superseded by versioned replacements — remove on first load
const LEGACY_KEYS_TO_PURGE = ['kfppc_active_match'];
import { APP_VERSION, INITIAL_ELO } from '../constants';


// Firestore Collection Reference
const DB_COLLECTION = 'leagues';
const DB_DOC = 'kfppc_v14_prod';

// Simple hash function for state comparison
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(36);
};

// Re-export safeStringify for backward compatibility with imports in other files
export { safeStringify };

class DataManager {
  private store: GameStore;
  private _cachedAllMatches: any[] | null = null;
  private isSyncingFromRemote: boolean = false;
  private saveTimeout: any = null;
  
  // Concurrency control for cloud saves
  private isSaving: boolean = false;
  private hasPendingSave: boolean = false;

  // Separate debounce from backoff: debounce is for user changes, backoff is for error recovery
  private readonly DEBOUNCE_DELAY: number = 2000; // Fixed 2s debounce for user changes
  private retryDelay: number = 2000; // Starts at 2s, increases on errors
  private readonly MAX_RETRY_DELAY: number = 120000; // Cap at 2 minutes
  private isInErrorBackoff: boolean = false; // Track if we're in error recovery mode
  private localStorageTimeout: any = null; // Debounce timeout for localStorage writes
  private readonly LOCAL_STORAGE_DEBOUNCE: number = 2000; // Match cloud debounce
  
  // Flow control flags
  private hasPendingCloudWrites: boolean = false; // Reported by Firestore SDK
  private optimisticPendingWrite: boolean = false; // Internal flag to close race gap
  private dirtyCollections: Set<string> = new Set(); // Per-collection dirty tracking

  // Computed getter for isDirty to maintain backward compatibility
  private get isDirty(): boolean {
    return this.dirtyCollections.size > 0;
  }
  
  // CRITICAL: Prevent overwriting cloud data on fresh load
  private isInitialSyncComplete: boolean = false; 
  
  // Diffing to prevent redundant uploads
  private lastSavedStateStr: string = "";
  private lastStateForCache: GameState | null = null;

  // Hash check to prevent localStorage staleness
  private lastPersistedLocalHash: string = "";

  constructor() {
    // 1. Load Initial State from LocalStorage (Fast Load)
    // We keep this to render UI immediately while Firestore connects
    const initialState: GameState = {
      version: APP_VERSION,
      mode: 'singles', // Default, logic usually handles this independently
      players: storageAdapter.getItem(STORAGE_KEYS.PLAYERS, ROSTER),
      tournaments: storageAdapter.getItem(STORAGE_KEYS.TOURNAMENTS, PAST_TOURNAMENTS),
      quickMatches: storageAdapter.getItem(STORAGE_KEYS.MATCHES, []),
      news: storageAdapter.getItem(STORAGE_KEYS.NEWS, INITIAL_NEWS),
      
      teams: storageAdapter.getItem(STORAGE_KEYS.TEAMS, INITIAL_TEAMS),
      doublesTournaments: storageAdapter.getItem(STORAGE_KEYS.TOURNAMENTS_DOUBLES, PAST_DOUBLES_TOURNAMENTS),
      doublesQuickMatches: storageAdapter.getItem(STORAGE_KEYS.MATCHES_DOUBLES, []),
      doublesNews: storageAdapter.getItem(STORAGE_KEYS.NEWS_DOUBLES, INITIAL_DOUBLES_NEWS),
      
      customTickerMessage: storageAdapter.getItem(STORAGE_KEYS.TICKER, ""),
      commissionerReport: storageAdapter.getItem(STORAGE_KEYS.REPORT, null),
      theme: storageAdapter.getItem(STORAGE_KEYS.THEME, 'void'),
      appLogo: storageAdapter.getItem(STORAGE_KEYS.LOGO, null),
      connectionStatus: 'connecting', // Default state
    };

    // 2. Purge legacy localStorage keys that were replaced by versioned equivalents
    LEGACY_KEYS_TO_PURGE.forEach(k => { try { localStorage.removeItem(k); } catch {} });

    // 3. Initialize Store
    this.store = new GameStore(initialState);
    this.lastStateForCache = initialState;

    // 3. Initialize Firestore Sync (Deferred: initialized by useAuth upon authentication)
    // this.initFirestore();

    // 4. Subscribe to Store changes to Persist Data
    this.store.subscribe(() => {
      const currentState = this.store.getState();

      // Smart Cache Invalidation: Only invalidate if relevant data changed
      if (this.shouldInvalidateMatchCache(this.lastStateForCache, currentState)) {
        this._cachedAllMatches = null;
      }

      // Track which specific collections changed to enable per-collection dirty tracking
      if (this.lastStateForCache) {
        if (this.lastStateForCache.players !== currentState.players) this.dirtyCollections.add('players');
        if (this.lastStateForCache.tournaments !== currentState.tournaments) this.dirtyCollections.add('tournaments');
        if (this.lastStateForCache.quickMatches !== currentState.quickMatches) this.dirtyCollections.add('quickMatches');
        if (this.lastStateForCache.news !== currentState.news) this.dirtyCollections.add('news');
        if (this.lastStateForCache.teams !== currentState.teams) this.dirtyCollections.add('teams');
        if (this.lastStateForCache.doublesTournaments !== currentState.doublesTournaments) this.dirtyCollections.add('doublesTournaments');
        if (this.lastStateForCache.doublesQuickMatches !== currentState.doublesQuickMatches) this.dirtyCollections.add('doublesQuickMatches');
        if (this.lastStateForCache.doublesNews !== currentState.doublesNews) this.dirtyCollections.add('doublesNews');
        if (this.lastStateForCache.customTickerMessage !== currentState.customTickerMessage) this.dirtyCollections.add('customTickerMessage');
        if (this.lastStateForCache.commissionerReport !== currentState.commissionerReport) this.dirtyCollections.add('commissionerReport');
        if (this.lastStateForCache.appLogo !== currentState.appLogo) this.dirtyCollections.add('appLogo');
      }

      this.lastStateForCache = currentState;

      // Update LocalStorage with debounce for offline capability
      this.schedulePersistLocal();

      // CRITICAL: Don't schedule cloud save if the update came from the cloud.
      // This prevents the infinite loop of Cloud -> Local -> Cloud -> Local
      if (this.isSyncingFromRemote) return;

      // Debounce Cloud Save to prevent write spam
      // Use fixed debounce for user changes - backoff only applies to error retries
      if (this.saveTimeout) clearTimeout(this.saveTimeout);

      this.saveTimeout = setTimeout(() => {
        this.processCloudSaveQueue();
      }, this.DEBOUNCE_DELAY);
    });
  }

  private shouldInvalidateMatchCache(prev: GameState | null, curr: GameState): boolean {
    if (!prev) return true;
    if (prev.mode !== curr.mode) return true;
    if (prev.quickMatches !== curr.quickMatches) return true;
    if (prev.doublesQuickMatches !== curr.doublesQuickMatches) return true;
    if (prev.tournaments !== curr.tournaments) return true;
    if (prev.doublesTournaments !== curr.doublesTournaments) return true;

    // Only invalidate for player changes that affect match display (name, elo, stats)
    // Skip invalidation for cosmetic changes (avatar, hometown, etc.)
    if (prev.players !== curr.players) {
      return this.hasRelevantPlayerChanges(prev.players, curr.players);
    }
    if (prev.teams !== curr.teams) {
      return this.hasRelevantPlayerChanges(prev.teams, curr.teams);
    }

    return false;
  }

  private hasRelevantPlayerChanges(prev: Player[], curr: Player[]): boolean {
    if (prev.length !== curr.length) return true;

    for (let i = 0; i < prev.length; i++) {
      const p = prev[i];
      const c = curr.find(x => x.id === p.id);

      // Player added or removed
      if (!c) return true;

      // Check only match-relevant fields
      if (p.name !== c.name) return true;
      if (p.elo !== c.elo) return true;
      if (p.stats !== c.stats) return true;

      // Note: We intentionally skip avatar, hometown, badges, advancedStats, isArchived, isGuest
      // These don't affect the match cache display
    }

    return false;
  }

  private initFirestore() {
    console.log("KFPPC: Initializing Cloud Sync...");

    // Unsubscribe previous listener to prevent accumulation on reconnect
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }

    const docRef = doc(db, DB_COLLECTION, DB_DOC);

    // Enable metadata changes to detect when writes are flushed to server (Flow Control)
    // V8 Syntax: ref.onSnapshot(options, observer)
    this.firestoreUnsubscribe = onSnapshot(docRef, { includeMetadataChanges: true }, (snapshot: any) => {
        // Update backpressure flag
        const wasPending = this.hasPendingCloudWrites || this.optimisticPendingWrite;
        this.hasPendingCloudWrites = snapshot.metadata.hasPendingWrites;

        // If the SDK says clear, we can clear our optimistic flag too
        if (!this.hasPendingCloudWrites) {
            this.optimisticPendingWrite = false;
        }

        const isNowPending = this.hasPendingCloudWrites || this.optimisticPendingWrite;

        // Flow Control Trigger
        if (wasPending && !isNowPending && this.hasPendingSave) {
            if (this.saveTimeout) clearTimeout(this.saveTimeout);
            this.saveTimeout = setTimeout(() => this.processCloudSaveQueue(), this.retryDelay);
        }

        // Prevent feedback loop: If the snapshot has pending writes, it means *we* just wrote it locally.
        if (snapshot.metadata.hasPendingWrites) return;

        // CRITICAL FIX: Set sync flag BEFORE checking dirty collections to prevent race condition.
        // This ensures any local changes made during our check will see the flag is set.
        this.isSyncingFromRemote = true;

        try {
            // CRITICAL: If we have pending local changes for specific collections,
            // only accept remote updates for non-dirty collections.
            // This allows Device A's changes to other collections to sync down while
            // protecting Device A's in-progress changes.
            // EXCEPTION: On initial sync, always accept all cloud data to ensure Firestore is source of truth
            if (this.dirtyCollections.size > 0 && this.isInitialSyncComplete) {
                // Partial sync: accept remote data for non-dirty collections only
                if (snapshot.exists) {
                    const remoteData = snapshot.data() as Partial<GameState> & { version?: string };
                    const updates: Partial<GameState> = { connectionStatus: 'connected' };

                    if (!this.dirtyCollections.has('players') && remoteData.players) updates.players = remoteData.players;
                    if (!this.dirtyCollections.has('tournaments') && remoteData.tournaments) updates.tournaments = remoteData.tournaments;
                    if (!this.dirtyCollections.has('quickMatches') && remoteData.quickMatches) updates.quickMatches = remoteData.quickMatches;
                    if (!this.dirtyCollections.has('news') && remoteData.news) updates.news = remoteData.news;
                    if (!this.dirtyCollections.has('teams') && remoteData.teams) updates.teams = remoteData.teams;
                    if (!this.dirtyCollections.has('doublesTournaments') && remoteData.doublesTournaments) updates.doublesTournaments = remoteData.doublesTournaments;
                    if (!this.dirtyCollections.has('doublesQuickMatches') && remoteData.doublesQuickMatches) updates.doublesQuickMatches = remoteData.doublesQuickMatches;
                    if (!this.dirtyCollections.has('doublesNews') && remoteData.doublesNews) updates.doublesNews = remoteData.doublesNews;
                    if (!this.dirtyCollections.has('customTickerMessage') && remoteData.customTickerMessage) updates.customTickerMessage = remoteData.customTickerMessage || "";
                    if (!this.dirtyCollections.has('commissionerReport') && remoteData.commissionerReport) updates.commissionerReport = remoteData.commissionerReport || null;
                    if (!this.dirtyCollections.has('appLogo') && remoteData.appLogo) updates.appLogo = remoteData.appLogo || null;

                    if (Object.keys(updates).length > 1) { // > 1 because connectionStatus is always there
                        this.store.setState(updates);
                    }
                }
                return; // Still return to not clobber dirty collections
            }

            if (snapshot.exists) {
                // REMOTE DATA EXISTS: SYNC DOWN (full sync when no dirty collections)
                const remoteData = snapshot.data() as Partial<GameState> & { version?: string };
                remoteData.version = remoteData.version || APP_VERSION;

                this.store.setState({
                    version: remoteData.version || APP_VERSION,
                    players: remoteData.players || [],
                    tournaments: remoteData.tournaments || [],
                    quickMatches: remoteData.quickMatches || [],
                    news: remoteData.news || [],
                    teams: remoteData.teams || [],
                    doublesTournaments: remoteData.doublesTournaments || [],
                    doublesQuickMatches: remoteData.doublesQuickMatches || [],
                    doublesNews: remoteData.doublesNews || [],
                    customTickerMessage: remoteData.customTickerMessage || "",
                    commissionerReport: remoteData.commissionerReport || null,
                    appLogo: remoteData.appLogo || null,
                    connectionStatus: 'connected',
                });

                // Update our last known state string to prevent echoing this back
                const cloudPayload = {
                    version: remoteData.version || APP_VERSION,
                    players: remoteData.players,
                    tournaments: remoteData.tournaments,
                    quickMatches: remoteData.quickMatches,
                    news: remoteData.news,
                    teams: remoteData.teams,
                    doublesTournaments: remoteData.doublesTournaments,
                    doublesQuickMatches: remoteData.doublesQuickMatches,
                    doublesNews: remoteData.doublesNews,
                    customTickerMessage: remoteData.customTickerMessage,
                    commissionerReport: remoteData.commissionerReport,
                    appLogo: remoteData.appLogo,
                    updatedAt: (remoteData as any).updatedAt
                };
                this.lastSavedStateStr = safeStringify(cloudPayload);
            } else {
                // REMOTE DATA DOES NOT EXIST: SEED UP
                // Only in this specific case do we force push immediately
                console.log("KFPPC: No cloud data found. Seeding database with current state...");
                this.persistCloud(true);
                this.store.setState({ connectionStatus: 'connected' });
            }

            // MARK INITIAL SYNC COMPLETE
            this.isInitialSyncComplete = true;

            // Reset connection recovery state on successful sync
            this.resetConnectionRecovery();
        } finally {
            // Always reset the sync flag to allow future local changes
            this.isSyncingFromRemote = false;
        }

    }, (error: any) => {
        console.error("KFPPC: Firestore Sync Error", error);
        this.store.setState({ connectionStatus: 'error' });

        // CRITICAL: Mark initial sync as complete even on error
        // This allows the app to load with localStorage data (offline mode)
        // Otherwise the loading gate in App.tsx will block forever
        if (!this.isInitialSyncComplete) {
            this.isInitialSyncComplete = true;
            console.warn("KFPPC: Operating in offline mode with localStorage data");
        }

        // Attempt connection recovery with exponential backoff
        this.scheduleConnectionRecovery();
    });
  }

  private connectionRecoveryAttempts: number = 0;
  private connectionRecoveryTimeout: any = null;
  private firestoreUnsubscribe: (() => void) | null = null;
  private readonly MAX_RECOVERY_ATTEMPTS: number = 5;

  private scheduleConnectionRecovery() {
    // Clear any existing recovery timeout
    if (this.connectionRecoveryTimeout) {
      clearTimeout(this.connectionRecoveryTimeout);
    }

    // Check if we've exceeded max attempts
    if (this.connectionRecoveryAttempts >= this.MAX_RECOVERY_ATTEMPTS) {
      console.warn("KFPPC: Max connection recovery attempts reached. Operating in offline mode.");
      this.store.setState({ connectionStatus: 'offline' });

      // Ensure initial sync is marked complete so app can load with localStorage
      if (!this.isInitialSyncComplete) {
        this.isInitialSyncComplete = true;
      }

      return;
    }

    // Calculate backoff delay: 5s, 10s, 20s, 40s, 80s
    const backoffDelay = Math.min(5000 * Math.pow(2, this.connectionRecoveryAttempts), 80000);
    this.connectionRecoveryAttempts++;

    console.log(`KFPPC: Scheduling connection recovery attempt ${this.connectionRecoveryAttempts} in ${backoffDelay}ms`);

    this.connectionRecoveryTimeout = setTimeout(() => {
      console.log("KFPPC: Attempting to reconnect to Firestore...");
      this.store.setState({ connectionStatus: 'connecting' });

      // Re-initialize Firestore listener
      // The old listener will be garbage collected
      this.initFirestore();
    }, backoffDelay);
  }

  // Reset recovery state on successful connection
  private resetConnectionRecovery() {
    this.connectionRecoveryAttempts = 0;
    if (this.connectionRecoveryTimeout) {
      clearTimeout(this.connectionRecoveryTimeout);
      this.connectionRecoveryTimeout = null;
    }

    // Pull latest data from cloud to ensure we have the most recent state
    try {
      this.forceRefreshFromCloud();
    } catch (e) {
      console.warn('KFPPC: Force refresh after reconnection failed', e);
      // Continue anyway - the regular sync will catch up
    }
  }

  private schedulePersistLocal() {
    // Debounce localStorage writes to reduce write spam
    if (this.localStorageTimeout) clearTimeout(this.localStorageTimeout);

    this.localStorageTimeout = setTimeout(() => {
      this.persistLocalImmediate();
    }, this.LOCAL_STORAGE_DEBOUNCE);
  }

  private persistLocalImmediate() {
    const state = this.store.getState();

    // Limit news arrays in localStorage too (not just Firestore)
    const limitedNews = state.news.slice(0, 30);
    const limitedDoublesNews = state.doublesNews.slice(0, 30);

    // Prune point-by-point history from old matches to reduce localStorage size
    const singles = this.pruneMatchHistory(state.quickMatches, state.tournaments);
    const doubles = this.pruneMatchHistory(state.doublesQuickMatches, state.doublesTournaments);

    // Create a hash of the state being persisted to avoid redundant writes
    const stateToHash = safeStringify({
      players: state.players,
      tournaments: singles.prunedTournaments,
      quickMatches: singles.prunedMatches,
      news: limitedNews,
      teams: state.teams,
      doublesTournaments: doubles.prunedTournaments,
      doublesQuickMatches: doubles.prunedMatches,
      doublesNews: limitedDoublesNews,
      customTickerMessage: state.customTickerMessage,
      commissionerReport: state.commissionerReport,
      appLogo: state.appLogo,
      theme: state.theme
    });

    const currentHash = simpleHash(stateToHash);

    // Skip write if state hasn't changed
    if (currentHash === this.lastPersistedLocalHash) {
      return;
    }

    storageAdapter.setItem(STORAGE_KEYS.PLAYERS, state.players);
    storageAdapter.setItem(STORAGE_KEYS.TOURNAMENTS, singles.prunedTournaments);
    storageAdapter.setItem(STORAGE_KEYS.MATCHES, singles.prunedMatches);
    storageAdapter.setItem(STORAGE_KEYS.NEWS, limitedNews);

    storageAdapter.setItem(STORAGE_KEYS.TEAMS, state.teams);
    storageAdapter.setItem(STORAGE_KEYS.TOURNAMENTS_DOUBLES, doubles.prunedTournaments);
    storageAdapter.setItem(STORAGE_KEYS.MATCHES_DOUBLES, doubles.prunedMatches);
    storageAdapter.setItem(STORAGE_KEYS.NEWS_DOUBLES, limitedDoublesNews);

    storageAdapter.setItem(STORAGE_KEYS.TICKER, state.customTickerMessage);

    if (state.commissionerReport) storageAdapter.setItem(STORAGE_KEYS.REPORT, state.commissionerReport);
    else storageAdapter.removeItem(STORAGE_KEYS.REPORT);

    if (state.appLogo) storageAdapter.setItem(STORAGE_KEYS.LOGO, state.appLogo);
    else storageAdapter.removeItem(STORAGE_KEYS.LOGO);

    storageAdapter.setItem(STORAGE_KEYS.THEME, state.theme);

    // Update the hash after successful write
    this.lastPersistedLocalHash = currentHash;
  }

  private async processCloudSaveQueue() {
      // 0. SYNC GUARD
      if (!this.isInitialSyncComplete) {
          this.hasPendingSave = true;
          return;
      }

      // 1. Backpressure Check
      if (this.hasPendingCloudWrites || this.optimisticPendingWrite) {
          this.hasPendingSave = true; 
          return;
      }

      // 2. Local Lock
      if (this.isSaving) {
          this.hasPendingSave = true;
          return;
      }

      this.isSaving = true;
      this.hasPendingSave = false;

      try {
          await this.persistCloud();
          // Success: Reset backoff state
          this.retryDelay = 2000;
          this.isInErrorBackoff = false;
      } catch (e: any) {
          console.error("KFPPC: Error processing save queue", e);

          // Permission errors will never resolve by retrying — stop immediately
          if (e.code === 'permission-denied' || e.code === 'PERMISSION_DENIED') {
              console.error("KFPPC: Write permission denied. User role may not have write access to Firestore.");
              this.isInErrorBackoff = true;
              return;
          }

          this.hasPendingSave = true; // Ensure we retry
          this.isInErrorBackoff = true;

          // Exponential backoff for retries only
          if (e.code === 'resource-exhausted' || (e.message && e.message.includes('exhausted'))) {
              this.retryDelay = Math.min(this.retryDelay * 2, this.MAX_RETRY_DELAY);
              console.warn(`KFPPC: Cloud save backed off to ${this.retryDelay}ms due to congestion`);
          } else {
              // For other errors, use a moderate backoff
              this.retryDelay = Math.min(this.retryDelay * 1.5, 30000);
          }
      } finally {
          this.isSaving = false;
          
          // 3. Retry Logic
          if (this.hasPendingSave) {
              if (!this.hasPendingCloudWrites && !this.optimisticPendingWrite) {
                  if (this.saveTimeout) clearTimeout(this.saveTimeout);
                  this.saveTimeout = setTimeout(() => this.processCloudSaveQueue(), this.retryDelay);
              }
          }
      }
  }

  /** Tiered history pruning: full (recent) → slim/graph-only (mid) → none (old). */
  private pruneMatchHistory(quickMatches: Match[], tournaments: Tournament[], retainFull = 50, retainSlim = 200) {
      const slimHistory = (h: any[]) => h.map((p: any) => {
          const slim: any = { score1: p.score1, score2: p.score2, winProb1: p.winProb1 };
          if (p.server) slim.server = p.server;
          if (p.tag) slim.tag = p.tag;
          return slim;
      });
      const total = quickMatches.length;
      const prunedMatches = quickMatches.map((m, i) => {
          const age = total - 1 - i; // 0 = newest, total-1 = oldest
          if (age < retainFull) return m;
          if (age < retainSlim && m.history) return { ...m, history: slimHistory(m.history) };
          const { history, ...rest } = m;
          return rest as Match;
      });
      const prunedTournaments = tournaments.map(t => {
          if (t.status === TournamentStatus.ACTIVE) return t;
          return { ...t, matches: t.matches.map(m => {
              if (!m.history) return m;
              return { ...m, history: slimHistory(m.history) };
          }) };
      });
      return { prunedMatches, prunedTournaments };
  }

  private async persistCloud(force: boolean = false) {
      if (this.isSyncingFromRemote && !force) return;

      // Guard: Do not attempt to save to cloud if user is not authenticated
      if (!auth.currentUser) {
          return;
      }

      const state = this.store.getState();

      // Prune point-by-point history from old matches to stay under Firestore 1MB doc limit
      const singles = this.pruneMatchHistory(state.quickMatches, state.tournaments);
      const doubles = this.pruneMatchHistory(state.doublesQuickMatches, state.doublesTournaments);

      const cloudPayload = {
          version: state.version,
          players: state.players,
          tournaments: singles.prunedTournaments,
          quickMatches: singles.prunedMatches,
          // Limit news to recent 30 to prevent payload explosion
          news: state.news.slice(0, 30),
          teams: state.teams,
          doublesTournaments: doubles.prunedTournaments,
          doublesQuickMatches: doubles.prunedMatches,
          doublesNews: state.doublesNews.slice(0, 30),
          customTickerMessage: state.customTickerMessage,
          commissionerReport: state.commissionerReport,
          appLogo: state.appLogo,
          updatedAt: new Date().toISOString()
      };

      // DIFF CHECK: Prevent writing if payload hasn't semantically changed
      const payloadString = safeStringify({ ...cloudPayload, updatedAt: '' });
      const lastSavedString = safeStringify({ ...JSON.parse(this.lastSavedStateStr || '{}'), updatedAt: '' });

      if (!force && payloadString === lastSavedString) {
          this.dirtyCollections.clear();
          return;
      }

      try {
          this.optimisticPendingWrite = true;
          
          await setDoc(doc(db, DB_COLLECTION, DB_DOC), cloudPayload);
          
          this.lastSavedStateStr = safeStringify(cloudPayload); // Update baseline
          this.dirtyCollections.clear();
          this.store.setState({ connectionStatus: 'connected' });
      } catch (e: any) {
          const isPermissionError = e.code === 'permission-denied' || e.code === 'PERMISSION_DENIED';
          console.error(
              isPermissionError
                  ? "KFPPC: Cloud Save BLOCKED by permissions. User needs write access to Firestore."
                  : "KFPPC: Cloud Save Failed",
              e
          );
          this.store.setState({ connectionStatus: 'error' });
          this.optimisticPendingWrite = false; // Reset if it failed synchronously
          throw e;
      }
  }

  public forceSave() {
      if (this.saveTimeout) clearTimeout(this.saveTimeout);
      this.processCloudSaveQueue();
  }

  /**
   * Re-initialize the Firestore listener with fresh auth credentials.
   * Called by useAuth when Firebase Auth confirms a valid session,
   * ensuring the listener connects after auth is ready (fixes race condition
   * where DataManager constructor runs before auth token is restored).
   */
  public reconnectFirestore() {
      this.isInitialSyncComplete = false;
      this.dirtyCollections.clear();
      this.connectionRecoveryAttempts = 0;
      if (this.connectionRecoveryTimeout) {
          clearTimeout(this.connectionRecoveryTimeout);
          this.connectionRecoveryTimeout = null;
      }
      this.initFirestore();
  }

  /**
   * Force refresh from Firestore, discarding all local changes.
   * Useful when user wants to reset to cloud state.
   */
  public async forceRefreshFromCloud(): Promise<void> {
    try {
      const docRef = doc(db, DB_COLLECTION, DB_DOC);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        console.warn('No cloud data found to refresh from');
        return;
      }

      // Clear dirty collections and sync from cloud
      this.dirtyCollections.clear();
      this.isSyncingFromRemote = true;

      try {
        const remoteData = snapshot.data() as Partial<GameState> & { version?: string };
        remoteData.version = remoteData.version || APP_VERSION;

        this.store.setState({
          version: remoteData.version || APP_VERSION,
          players: remoteData.players || [],
          tournaments: remoteData.tournaments || [],
          quickMatches: remoteData.quickMatches || [],
          news: remoteData.news || [],
          teams: remoteData.teams || [],
          doublesTournaments: remoteData.doublesTournaments || [],
          doublesQuickMatches: remoteData.doublesQuickMatches || [],
          doublesNews: remoteData.doublesNews || [],
          customTickerMessage: remoteData.customTickerMessage || "",
          commissionerReport: remoteData.commissionerReport || null,
          appLogo: remoteData.appLogo || null,
          connectionStatus: 'connected',
        });

        const cloudPayload = {
          version: remoteData.version || APP_VERSION,
          players: remoteData.players,
          tournaments: remoteData.tournaments,
          quickMatches: remoteData.quickMatches,
          news: remoteData.news,
          teams: remoteData.teams,
          doublesTournaments: remoteData.doublesTournaments,
          doublesQuickMatches: remoteData.doublesQuickMatches,
          doublesNews: remoteData.doublesNews,
          customTickerMessage: remoteData.customTickerMessage,
          commissionerReport: remoteData.commissionerReport,
          appLogo: remoteData.appLogo,
          updatedAt: (remoteData as any).updatedAt
        };
        this.lastSavedStateStr = safeStringify(cloudPayload);

        console.log('KFPPC: Force refresh from cloud completed');
      } finally {
        this.isSyncingFromRemote = false;
      }
    } catch (error) {
      console.error('KFPPC: Force refresh failed', error);
      this.store.setState({ connectionStatus: 'error' });
      throw error;
    }
  }

  // --- Public API (Facade) ---

  subscribe(listener: () => void) {
    return this.store.subscribe(listener);
  }

  setGameMode(mode: GameMode) {
    this.store.setMode(mode);
  }

  getGameMode = () => {
    return this.store.getState().mode;
  }

  getConnectionStatus = () => {
    return this.store.getState().connectionStatus;
  }

  getIsInitialSyncComplete = () => {
    return this.isInitialSyncComplete;
  }

  // Context-Aware Getters
  getPlayers = (): Player[] => {
    const state = this.store.getState();
    return state.mode === 'doubles' ? [...state.teams] : [...state.players];
  }

  getSinglesPlayers = (): Player[] => {
    return [...this.store.getState().players];
  }

  getTeams = (): Team[] => {
    return [...this.store.getState().teams];
  }

  getPlayer = (id: string): Player | undefined => {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      const t = state.teams.find((t: Team) => t.id === id);
      return t ? JSON.parse(JSON.stringify(t)) : undefined;
    }
    const p = state.players.find((p: Player) => p.id === id);
    return p ? JSON.parse(JSON.stringify(p)) : undefined;
  }

  getTournaments = () => {
    const state = this.store.getState();
    return state.mode === 'doubles' ? [...state.doublesTournaments] : [...state.tournaments];
  }

  getTournament = (id: string) => {
    const state = this.store.getState();
    const list = state.mode === 'doubles' ? state.doublesTournaments : state.tournaments;
    const t = list.find((t: Tournament) => t.id === id);
    return t ? JSON.parse(JSON.stringify(t)) : undefined;
  }

  getNews = () => {
    const state = this.store.getState();
    return state.mode === 'doubles' ? [...state.doublesNews] : [...state.news];
  }

  getCustomTickerMessage = () => { return this.store.getState().customTickerMessage; }
  getCommissionerReport = () => { return this.store.getState().commissionerReport; }
  getTheme = () => { return this.store.getState().theme; }
  getAppLogo = () => { return this.store.getState().appLogo; }

  getDataVersion = () => {
    return this.store.getState().version;
  }

  // --- Logic / Aggregation ---

  getAllMatches = () => {
    if (this._cachedAllMatches) {
      return this._cachedAllMatches;
    }

    const state = this.store.getState();
    const currentPlayers = this.getPlayers();
    const currentTournaments = this.getTournaments();
    const currentQuickMatches = state.mode === 'doubles' ? state.doublesQuickMatches : state.quickMatches;

    const playerMap = new Map<string, string>();
    currentPlayers.forEach(p => playerMap.set(p.id, p.name));

    const getPlayerName = (id: string) => {
      if (id === 'BYE') return 'BYE';
      if (id === 'TBD') return 'TBD';
      return playerMap.get(id) || id;
    };

    const tournamentMatches = currentTournaments.flatMap((t: Tournament) =>
      t.matches.map((m: TournamentMatch, idx: number) => {
        let ts = 0;
        if (m.history && m.history.length > 0) {
           ts = m.history[m.history.length - 1].timestamp;
        } else if (m.id) {
           const parts = m.id.split('_');
           if (parts.length > 1 && !isNaN(Number(parts[1]))) {
              ts = Number(parts[1]);
           }
        }

        if ((!ts || ts === 0) && t.date) {
            ts = new Date(t.date).getTime();
        }

        return {
          ...m,
          p1Name: getPlayerName(m.p1),
          p2Name: getPlayerName(m.p2),
          tournamentId: t.id,
          tournamentName: t.name,
          season: t.season,
          year: t.year,
          isMajor: t.isMajor,
          date: `${t.season} ${t.year}`,
          isGuest: false,
          id: m.id || `${t.id}_m_${idx}`,
          timestamp: ts,
          media: m.media
        };
      })
    );

    const standalone = currentQuickMatches.map(m => ({
      ...m,
      p1: m.player1Id,
      p2: m.player2Id,
      p1Name: getPlayerName(m.player1Id),
      p2Name: getPlayerName(m.player2Id),
      s1: m.score1,
      s2: m.score2,
      winner: m.winnerId,
      round: 'Quick Match',
      tournamentName: 'Ranked Match',
      isMajor: false,
      date: new Date(m.startTime).toLocaleDateString(),
      timestamp: m.startTime,
    }));

    this._cachedAllMatches = [...tournamentMatches, ...standalone];

    return this._cachedAllMatches;
  }

  // --- Mutations (Forward to Store) ---

  batchUpdateMatchSnapshots(updates: { matchId: string, tournamentId: string | null, p1Elo: number, p2Elo: number }[]) {
    if (updates.length === 0) return;

    const state = this.store.getState();
    const isDoubles = state.mode === 'doubles';

    let quickMatchesChanged = false;
    let tournamentsChanged = false;
    let updatedCount = 0;

    const quickMatches = isDoubles ? [...state.doublesQuickMatches] : [...state.quickMatches];
    const tournaments = isDoubles ? [...state.doublesTournaments] : [...state.tournaments];

    updates.forEach(u => {
        if (u.tournamentId) {
            // Find tournament
            const tIdx = tournaments.findIndex(t => t.id === u.tournamentId);
            if (tIdx !== -1) {
                // Find match in tournament
                const matches = [...tournaments[tIdx].matches];
                const mIdx = matches.findIndex(m => m.id === u.matchId);
                if (mIdx !== -1) {
                    matches[mIdx] = { ...matches[mIdx], p1EloAfter: u.p1Elo, p2EloAfter: u.p2Elo };
                    tournaments[tIdx] = { ...tournaments[tIdx], matches };
                    tournamentsChanged = true;
                    updatedCount++;
                }
            }
        } else {
            // Find quick match
            const mIdx = quickMatches.findIndex(m => m.id === u.matchId);
            if (mIdx !== -1) {
                quickMatches[mIdx] = { ...quickMatches[mIdx], p1EloAfter: u.p1Elo, p2EloAfter: u.p2Elo };
                quickMatchesChanged = true;
                updatedCount++;
            }
        }
    });

    if (quickMatchesChanged) {
        if (isDoubles) this.store.setState({ doublesQuickMatches: quickMatches });
        else this.store.setState({ quickMatches });
    }

    if (tournamentsChanged) {
        if (isDoubles) this.store.setState({ doublesTournaments: tournaments });
        else this.store.setState({ tournaments });
    }
  }

  setPlayers(players: Player[]) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      this.store.setState({ teams: players as Team[] });
    } else {
      this.store.setState({ players });
    }
  }

  /**
   * Batch update for multiple players/teams.
   * Reduces re-renders and persistence calls compared to calling updatePlayer in a loop.
   */
  updatePlayers(updatedPlayers: Player[]) {
    if (!updatedPlayers.length) return;
    const state = this.store.getState();
    const isDoubles = state.mode === 'doubles';
    
    const list = isDoubles ? [...state.teams] : [...state.players];
    const idMap = new Map(list.map((item, idx) => [item.id, idx]));
    let changed = false;

    updatedPlayers.forEach(u => {
        const idx = idMap.get(u.id);
        if (idx !== undefined) {
            list[idx] = u as any;
            changed = true;
        } else {
            list.push(u as any);
            idMap.set(u.id, list.length - 1);
            changed = true;
        }
    });

    if (changed) {
        if (isDoubles) this.store.setState({ teams: list as Team[] });
        else this.store.setState({ players: list });
    }
  }

  /**
   * Transactional update for recording a quick match and updating involved players.
   */
  recordQuickMatch(match: Match, updatedPlayers: Player[]) {
    const state = this.store.getState();
    const isDoubles = state.mode === 'doubles';
    
    // 1. Update Players/Teams (Reuse logic but keep in one setState)
    const list = isDoubles ? [...state.teams] : [...state.players];
    const idMap = new Map(list.map((item, idx) => [item.id, idx]));
    
    updatedPlayers.forEach(u => {
        const idx = idMap.get(u.id);
        if (idx !== undefined) list[idx] = u as any;
        else { list.push(u as any); idMap.set(u.id, list.length - 1); }
    });

    // 2. Add Match
    const matches = isDoubles ? [...state.doublesQuickMatches, match] : [...state.quickMatches, match];

    // 3. Commit
    if (isDoubles) {
        this.store.setState({ teams: list as Team[], doublesQuickMatches: matches });
    } else {
        this.store.setState({ players: list, quickMatches: matches });
    }
  }

  updatePlayer(updatedPlayer: Player) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      const teams = [...state.teams];
      const index = teams.findIndex(t => t.id === updatedPlayer.id);
      if (index !== -1) teams[index] = updatedPlayer as Team;
      else teams.push(updatedPlayer as Team);
      this.store.setState({ teams });
    } else {
      const players = [...state.players];
      const index = players.findIndex(p => p.id === updatedPlayer.id);
      if (index !== -1) players[index] = updatedPlayer;
      else players.push(updatedPlayer);
      this.store.setState({ players });
    }
  }

  updateTeam(team: Team) {
    const teams = [...this.store.getState().teams];
    const index = teams.findIndex(t => t.id === team.id);
    if (index !== -1) teams[index] = team;
    else teams.push(team);
    this.store.setState({ teams });
  }

  addPlayer(player: Player) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
       this.store.setState({ teams: [...state.teams, player as Team] });
    } else {
       this.store.setState({ players: [...state.players, player] });
    }
  }

  addTeam(team: Team) {
    this.store.setState({ teams: [...this.store.getState().teams, team] });
  }

  deleteTeam(id: string) {
    this.store.setState({ teams: this.store.getState().teams.filter(t => t.id !== id) });
  }

  addQuickMatch(match: Match) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      this.store.setState({ doublesQuickMatches: [...state.doublesQuickMatches, match] });
    } else {
      this.store.setState({ quickMatches: [...state.quickMatches, match] });
    }
  }

  deleteQuickMatch(id: string) {
    const state = this.store.getState();
    const sIdx = state.quickMatches.findIndex(m => m.id === id);
    if (sIdx !== -1) {
        const newMatches = [...state.quickMatches];
        newMatches.splice(sIdx, 1);
        this.store.setState({ quickMatches: newMatches });
        return;
    }
    const dIdx = state.doublesQuickMatches.findIndex(m => m.id === id);
    if (dIdx !== -1) {
        const newMatches = [...state.doublesQuickMatches];
        newMatches.splice(dIdx, 1);
        this.store.setState({ doublesQuickMatches: newMatches });
        return;
    }
  }

  updateQuickMatchScore(id: string, s1: number, s2: number) {
     const state = this.store.getState();
     
     const updateList = (list: Match[]) => {
         return list.map(m => {
             if (m.id === id) {
                 const winner = s1 > s2 ? m.player1Id : m.player2Id;
                 return { ...m, score1: s1, score2: s2, winnerId: winner };
             }
             return m;
         });
     };

     if (state.quickMatches.some(m => m.id === id)) {
         this.store.setState({ quickMatches: updateList(state.quickMatches) });
         return;
     }
     if (state.doublesQuickMatches.some(m => m.id === id)) {
         this.store.setState({ doublesQuickMatches: updateList(state.doublesQuickMatches) });
         return;
     }
  }

  addTournament(tournament: Tournament) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      this.store.setState({ doublesTournaments: [...state.doublesTournaments, tournament] });
    } else {
      this.store.setState({ tournaments: [...state.tournaments, tournament] });
    }
  }

  /**
   * Transactional update for tournament and players.
   */
  updateTournamentAndPlayers(tournament: Tournament, updatedPlayers: Player[]) {
    const state = this.store.getState();
    const isDoubles = state.mode === 'doubles';
    const updates: Partial<typeof state> = {};

    // Update Tournament
    const tList = isDoubles ? [...state.doublesTournaments] : [...state.tournaments];
    const tIdx = tList.findIndex(t => t.id === tournament.id);
    if (tIdx !== -1) {
        tList[tIdx] = tournament;
        if (isDoubles) updates.doublesTournaments = tList;
        else updates.tournaments = tList;
    }

    // Update Players (Delegate to internal logic if needed, but here we do it manually for atomicity)
    const pList = isDoubles ? [...state.teams] : [...state.players];
    const pMap = new Map(pList.map((p, i) => [p.id, i]));
    let pChanged = false;

    updatedPlayers.forEach(u => {
        const idx = pMap.get(u.id);
        if (idx !== undefined) { pList[idx] = u as any; pChanged = true; }
        else { pList.push(u as any); pMap.set(u.id, pList.length - 1); pChanged = true; }
    });

    if (pChanged) {
        if (isDoubles) updates.teams = pList as Team[];
        else updates.players = pList;
    }

    this.store.setState(updates);
  }

  updateTournament(tournament: Tournament) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      const list = [...state.doublesTournaments];
      const index = list.findIndex(t => t.id === tournament.id);
      if (index !== -1) {
        list[index] = tournament;
        this.store.setState({ doublesTournaments: list });
      }
    } else {
      const list = [...state.tournaments];
      const index = list.findIndex(t => t.id === tournament.id);
      if (index !== -1) {
        list[index] = tournament;
        this.store.setState({ tournaments: list });
      }
    }
  }

  deleteTournament(id: string) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      this.store.setState({ doublesTournaments: state.doublesTournaments.filter(t => t.id !== id) });
    } else {
      this.store.setState({ tournaments: state.tournaments.filter(t => t.id !== id) });
    }
  }

  addNews(article: NewsArticle) {
    const state = this.store.getState();
    if (state.mode === 'doubles') {
      const news = [article, ...state.doublesNews.map(n => n.isBreaking ? { ...n, isBreaking: false } : n)].slice(0, 50);
      this.store.setState({ doublesNews: news });
    } else {
      const news = [article, ...state.news.map(n => n.isBreaking ? { ...n, isBreaking: false } : n)].slice(0, 50);
      this.store.setState({ news });
    }
  }

  setCustomTickerMessage(message: string) {
    this.store.setState({ customTickerMessage: message });
  }

  setCommissionerReport(report: string | null) {
    this.store.setState({ commissionerReport: report });
  }

  setTheme(theme: 'void' | 'light') {
    this.store.setState({ theme });
  }

  setAppLogo(logo: string | null) {
    this.store.setState({ appLogo: logo });
  }

  addMediaToMatch(matchId: string, mediaItem: MatchMedia, tournamentId?: string) {
    const state = this.store.getState();
    const isDoubles = state.mode === 'doubles';
    
    const updateMatchInList = (matches: Match[]) => {
      const idx = matches.findIndex(m => m.id === matchId);
      if (idx !== -1) {
        const updatedMatches = [...matches];
        const match = { ...updatedMatches[idx] };
        match.media = match.media ? [...match.media, mediaItem] : [mediaItem];
        updatedMatches[idx] = match;
        return updatedMatches;
      }
      return null;
    };

    const updateTournamentInList = (tournaments: Tournament[]) => {
      const tIdx = tournaments.findIndex(t => tournamentId ? t.id === tournamentId : t.matches.some(m => m.id === matchId));
      if (tIdx !== -1) {
        const updatedTournaments = [...tournaments];
        const tournament = { ...updatedTournaments[tIdx] };
        const matches = [...tournament.matches];
        const mIdx = matches.findIndex(m => m.id === matchId);
        if (mIdx !== -1) {
           const match = { ...matches[mIdx] };
           match.media = match.media ? [...match.media, mediaItem] : [mediaItem];
           matches[mIdx] = match;
           tournament.matches = matches;
           updatedTournaments[tIdx] = tournament;
           return updatedTournaments;
        }
      }
      return null;
    };

    if (!tournamentId) {
       const newList = updateMatchInList(isDoubles ? state.doublesQuickMatches : state.quickMatches);
       if (newList) {
         if (isDoubles) this.store.setState({ doublesQuickMatches: newList });
         else this.store.setState({ quickMatches: newList });
         return;
       }
    }

    const newTournaments = updateTournamentInList(isDoubles ? state.doublesTournaments : state.tournaments);
    if (newTournaments) {
        if (isDoubles) this.store.setState({ doublesTournaments: newTournaments });
        else this.store.setState({ tournaments: newTournaments });
    }
  }

  deleteMediaFromMatch(matchId: string, mediaId: string, tournamentId?: string) {
    const state = this.store.getState();
    const isDoubles = state.mode === 'doubles';

    const updateMatchInList = (matches: Match[]) => {
      const idx = matches.findIndex(m => m.id === matchId);
      if (idx !== -1 && matches[idx].media) {
        const updatedMatches = [...matches];
        const match = { ...updatedMatches[idx] };
        match.media = match.media!.filter(m => m.id !== mediaId);
        updatedMatches[idx] = match;
        return updatedMatches;
      }
      return null;
    };

    const updateTournamentInList = (tournaments: Tournament[]) => {
        const tIdx = tournaments.findIndex(t => tournamentId ? t.id === tournamentId : t.matches.some(m => m.id === matchId));
        if (tIdx !== -1) {
            const updatedTournaments = [...tournaments];
            const tournament = { ...updatedTournaments[tIdx] };
            const matches = [...tournament.matches];
            const mIdx = matches.findIndex(m => m.id === matchId);
            if (mIdx !== -1 && matches[mIdx].media) {
                const match = { ...matches[mIdx] };
                match.media = match.media!.filter(m => m.id !== mediaId);
                matches[mIdx] = match;
                tournament.matches = matches;
                updatedTournaments[tIdx] = tournament;
                return updatedTournaments;
            }
        }
        return null;
    };

    if (!tournamentId) {
        const newList = updateMatchInList(isDoubles ? state.doublesQuickMatches : state.quickMatches);
        if (newList) {
            if (isDoubles) this.store.setState({ doublesQuickMatches: newList });
            else this.store.setState({ quickMatches: newList });
            return;
        }
    }

    const newTournaments = updateTournamentInList(isDoubles ? state.doublesTournaments : state.tournaments);
    if (newTournaments) {
        if (isDoubles) this.store.setState({ doublesTournaments: newTournaments });
        else this.store.setState({ tournaments: newTournaments });
    }
  }

  getBackupData() {
    const state = this.store.getState();
    return {
      version: APP_VERSION, 
      timestamp: Date.now(),
      mode: state.mode,
      players: state.players,
      teams: state.teams,
      tournaments: state.tournaments,
      doublesTournaments: state.doublesTournaments,
      matches: state.quickMatches,
      doublesMatches: state.doublesQuickMatches,
      news: state.news,
      doublesNews: state.doublesNews,
      customTickerMessage: state.customTickerMessage,
      commissionerReport: state.commissionerReport,
      theme: state.theme,
      appLogo: state.appLogo
    };
  }

  importBackupData(jsonString: string): boolean {
    try {
      const data = JSON.parse(jsonString);
      if (!Array.isArray(data.players) || !Array.isArray(data.tournaments)) {
        throw new Error("Invalid backup format");
      }
      
      this.store.setState({
        version: data.version || APP_VERSION,
        players: data.players,
        tournaments: data.tournaments,
        quickMatches: data.matches || [],
        teams: data.teams || [],
        doublesTournaments: data.doublesTournaments || [],
        doublesQuickMatches: data.doublesMatches || [],
        doublesNews: data.doublesNews || [],
        news: Array.isArray(data.news) ? data.news : [],
        customTickerMessage: typeof data.customTickerMessage === 'string' ? data.customTickerMessage : "",
        commissionerReport: typeof data.commissionerReport === 'string' ? data.commissionerReport : null,
        theme: data.theme === 'light' ? 'light' : 'void',
        appLogo: typeof data.appLogo === 'string' ? data.appLogo : null,
        mode: data.mode || 'singles'
      });
      
      return true;
    } catch (e) {
      console.error("Import failed", e);
      return false;
    }
  }

  async resetToSeed() {
    this.store.setState({
        version: APP_VERSION,
        players: [...ROSTER],
        tournaments: [...PAST_TOURNAMENTS],
        quickMatches: [],
        teams: [...INITIAL_TEAMS],
        doublesTournaments: [...PAST_DOUBLES_TOURNAMENTS],
        doublesQuickMatches: [],
        doublesNews: [...INITIAL_DOUBLES_NEWS],
        news: [...INITIAL_NEWS],
        customTickerMessage: "",
        commissionerReport: null,
        theme: 'void',
        appLogo: null,
        mode: 'singles'
    });

    // Cancel pending debounced saves and persist immediately — both to localStorage
    // and to Firestore — before reloading. Without this, window.location.reload() fires
    // before the 2-second debounces complete, so the cloud still holds the pre-reset data
    // and the onSnapshot listener restores it on the next page load.
    if (this.localStorageTimeout) clearTimeout(this.localStorageTimeout);
    this.persistLocalImmediate();

    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    await this.persistCloud(true);

    window.location.reload();
  }

  purgeAll() {
    const resetPlayers = ROSTER.map(p => ({
        ...p,
        elo: 50.0,
        stats: { wins: 0, losses: 0, championships: 0, pointsWon: 0, pointsLost: 0, matchesPlayed: 0, majorTitles: 0, minorTitles: 0 },
        advancedStats: { 
          clutchWins: 0, clutchTotal: 0, nemesisId: null, nemesisWinPct: 0, longestStreak: 0, currentStreak: 0, peakElo: 50.0, form: [],
          servicePointsWon: 0, servicePointsTotal: 0, returnPointsWon: 0, returnPointsTotal: 0,
          aces: 0, winners: 0, errors: 0, totalPointsPlayed: 0,
          deuceWins: 0, deuceTotal: 0, gamePointsConverted: 0, gamePointOpportunities: 0,
          cleanSheets: 0
        },
        badges: {}
    }));

    const resetTeams = INITIAL_TEAMS.map(t => ({
        ...t,
        elo: 50.0,
        stats: { wins: 0, losses: 0, championships: 0, pointsWon: 0, pointsLost: 0, matchesPlayed: 0, majorTitles: 0, minorTitles: 0 },
        advancedStats: { 
          clutchWins: 0, clutchTotal: 0, nemesisId: null, nemesisWinPct: 0, longestStreak: 0, currentStreak: 0, peakElo: 50.0, form: [],
          servicePointsWon: 0, servicePointsTotal: 0, returnPointsWon: 0, returnPointsTotal: 0,
          aces: 0, winners: 0, errors: 0, totalPointsPlayed: 0,
          deuceWins: 0, deuceTotal: 0, gamePointsConverted: 0, gamePointOpportunities: 0,
          cleanSheets: 0
        },
        badges: {}
    }));

    this.store.setState({
        version: APP_VERSION,
        players: resetPlayers,
        tournaments: [],
        quickMatches: [],
        teams: resetTeams,
        doublesTournaments: [],
        doublesQuickMatches: [],
        news: [],
        doublesNews: [],
        customTickerMessage: "",
        commissionerReport: null,
        theme: 'void',
        appLogo: null
    });
    window.location.reload();
  }

  async resetToSeedDirect(): Promise<void> {
    const seedPlayers = ROSTER.map(p => ({
      ...p,
      elo: INITIAL_ELO,
      stats: { wins: 0, losses: 0, championships: 0, pointsWon: 0, pointsLost: 0, matchesPlayed: 0, majorTitles: 0, minorTitles: 0 },
      advancedStats: {
        clutchWins: 0, clutchTotal: 0, nemesisId: null, nemesisWinPct: 0,
        longestStreak: 0, currentStreak: 0, peakElo: INITIAL_ELO, form: [],
        servicePointsWon: 0, servicePointsTotal: 0, returnPointsWon: 0, returnPointsTotal: 0,
        aces: 0, winners: 0, errors: 0, totalPointsPlayed: 0,
        deuceWins: 0, deuceTotal: 0, gamePointsConverted: 0, gamePointOpportunities: 0,
        cleanSheets: 0
      },
      badges: (MANUAL_BADGES[p.id] as Record<string, number>) || {}
    }));

    const seedTeams = INITIAL_TEAMS.map(t => ({
      ...t,
      elo: INITIAL_ELO,
      stats: { wins: 0, losses: 0, championships: 0, pointsWon: 0, pointsLost: 0, matchesPlayed: 0, majorTitles: 0, minorTitles: 0 },
      advancedStats: {
        clutchWins: 0, clutchTotal: 0, nemesisId: null, nemesisWinPct: 0,
        longestStreak: 0, currentStreak: 0, peakElo: INITIAL_ELO, form: [],
        servicePointsWon: 0, servicePointsTotal: 0, returnPointsWon: 0, returnPointsTotal: 0,
        aces: 0, winners: 0, errors: 0, totalPointsPlayed: 0,
        deuceWins: 0, deuceTotal: 0, gamePointsConverted: 0, gamePointOpportunities: 0,
        cleanSheets: 0
      },
      badges: {}
    }));

    const payload = {
      version: APP_VERSION,
      players: seedPlayers,
      tournaments: [...PAST_TOURNAMENTS],
      quickMatches: [],
      news: [...INITIAL_NEWS],
      teams: seedTeams,
      doublesTournaments: [...PAST_DOUBLES_TOURNAMENTS],
      doublesQuickMatches: [],
      doublesNews: [...INITIAL_DOUBLES_NEWS],
      customTickerMessage: "",
      commissionerReport: null,
      appLogo: null,
      updatedAt: new Date().toISOString()
    };

    // Write directly to Firestore and await — bypasses the debounce/race condition
    await setDoc(doc(db, DB_COLLECTION, DB_DOC), payload);

    // Clear localStorage so stale cached data doesn't interfere on reload
    localStorage.clear();

    window.location.reload();
  }
}

export const dataManager = new DataManager();
